import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createBilling, getBilling, updateBilling } from "../../../api/apiService";
import { getPatientById } from "../../../Services/patientService";
import { sanitizeText, containsExecutableMarkup, deepSanitizePayload } from "../../../utils/inputValidation";
import ToastContainer, { useToast } from "../../../components/common/ToastContainer";
import Trash from "../../../assets/images/comman/trash-outline.svg";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";
import BillingPatientList from "./BillingPatientList";

const Billing = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { toasts, removeToast, showSuccess, showError, showWarning } = useToast();
    const { can, user } = useAuth();
    const canWriteBilling = can(PERMISSIONS.BILLING_CREATE_INVOICE) || can(PERMISSIONS.BILLING_CREATE);
    const [patientData, setPatientData] = useState(null);
    const [billingId, setBillingId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isNavigatingAway, setIsNavigatingAway] = useState(false);

    const createNewItem = () => ({
        id: Date.now(),
        slNo: "",
        code: "",
        particulars: "",
        rate: "",
        unit: "",
        amount: "",
    });

    const [items, setItems] = useState([createNewItem()]);

    // Handle beforeunload to prevent form data loss when navigating away
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            const hasData = items.some(item => 
                item.particulars || item.rate || item.unit || item.amount
            );
            if (hasData && !isNavigatingAway) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [items, isNavigatingAway]);

    useEffect(() => {
        let isMounted = true;
        const loadPatient = async () => {
            let data = location.state?.patientData;
            const queryParams = new URLSearchParams(location.search);
            const patientIdFromQuery =
                queryParams.get("patientId") || location.state?.patientId;
            const targetId = data?._id || data?.id || patientIdFromQuery;

            if (!targetId) {
                if (isMounted) {
                    setPatientData(null);
                    setBillingId(null);
                    setItems([createNewItem()]);
                }
                return;
            }

            if (targetId && (!data || !data.visits || data.visits.length === 0)) {
                try {
                    const fetched = await getPatientById(targetId);
                    if (fetched && isMounted) {
                        data = {
                            ...(data || {}),
                            ...fetched,
                            visitId:
                                fetched.visitId ||
                                fetched.visits?.[0]?.visitId ||
                                data?.visitId,
                        };
                    }
                } catch (e) {
                    console.warn("Could not load patient for billing:", e);
                }
            }

            if (data && isMounted) {
                const fullName =
                    (data.name && typeof data.name === "string" && data.name.trim() && data.name.trim() !== "undefined undefined")
                        ? data.name.trim()
                        : (data.fullName && typeof data.fullName === "string" && data.fullName.trim() && data.fullName.trim() !== "undefined undefined")
                        ? data.fullName.trim()
                        : [data.f_name, data.m_name, data.l_name].filter(Boolean).join(" ") || "Patient";

                const isAbhaLinked = Boolean(
                    data.isAbhaLinked === true ||
                    data.isabhalinked === true ||
                    data.isABHALinked === true ||
                    String(data.isAbhaLinked).toLowerCase() === "true" ||
                    String(data.isabhalinked).toLowerCase() === "true" ||
                    String(data.isABHALinked).toLowerCase() === "true" ||
                    (data.maskedAbha && String(data.maskedAbha).trim() && data.maskedAbha !== "N/A") ||
                    (data.ABHANumber && String(data.ABHANumber).trim() && data.ABHANumber !== "N/A") ||
                    (data.abhaNumber && String(data.abhaNumber).trim() && data.abhaNumber !== "N/A") ||
                    (data.formattedAbhaId && String(data.formattedAbhaId).trim() && data.formattedAbhaId !== "N/A") ||
                    (data.abhaaddress && String(data.abhaaddress).trim()) ||
                    (data.abhaAddress && String(data.abhaAddress).trim()) ||
                    (data.ABHAAddress && String(data.ABHAAddress).trim())
                );

                const maskedAbha =
                    data.maskedAbha ||
                    (data.ABHANumber ? `**** ${String(data.ABHANumber).slice(-4)}` : "") ||
                    data.formattedAbhaId ||
                    (data.abhaaddress || data.abhaAddress || data.ABHAAddress || "");

                const enrichedPatient = {
                    ...data,
                    name: fullName,
                    fullName: fullName,
                    isAbhaLinked,
                    isabhalinked: isAbhaLinked,
                    maskedAbha,
                    formattedAbhaId: maskedAbha || data.ABHANumber || "",
                    visitId:
                        data.visitId ||
                        data.visits?.[0]?.visitId ||
                        data.visits?.[0]?._id ||
                        "",
                };

                setPatientData(enrichedPatient);
                fetchBillingForVisit(enrichedPatient);
            }
        };
        loadPatient();
        return () => {
            isMounted = false;
        };
    }, [location.state, location.search]);

    // Load existing billing for this visit (GET /api/billing/:id accepts visit id)
    const fetchBillingForVisit = async (patientInfo) => {
        const visitId = patientInfo?.visitId || patientInfo?.visits?.[0]?.visitId || "";
        if (!visitId) return;
        try {
            const res = await getBilling(visitId);
            const data = res?.data;
            if (data?.billings && Array.isArray(data.billings) && data.billings.length > 0) {
                setBillingId(data._id);
                setItems(
                    data.billings.map((b, i) => ({
                        id: Date.now() + i,
                        slNo: String(b.slNo ?? i + 1),
                        code: b.code ?? "",
                        particulars: b.particulars ?? "",
                        rate: b.rate != null ? String(b.rate) : "",
                        unit: b.unit != null ? String(b.unit) : "",
                        amount: b.amount != null ? String(b.amount) : "",
                    }))
                );
            }
        } catch (err) {
            console.error("Error fetching billing:", err);
        }
    };

    const handleSelectPatient = (selectedRawData, effectivePatientId) => {
        const fullName =
            (selectedRawData.name && typeof selectedRawData.name === "string" && selectedRawData.name.trim() && selectedRawData.name.trim() !== "undefined undefined")
                ? selectedRawData.name.trim()
                : (selectedRawData.fullName && typeof selectedRawData.fullName === "string" && selectedRawData.fullName.trim() && selectedRawData.fullName.trim() !== "undefined undefined")
                ? selectedRawData.fullName.trim()
                : [selectedRawData.f_name, selectedRawData.m_name, selectedRawData.l_name].filter(Boolean).join(" ") || "Patient";

        const isAbhaLinked = Boolean(
            selectedRawData.isAbhaLinked === true ||
            selectedRawData.isabhalinked === true ||
            selectedRawData.isABHALinked === true ||
            String(selectedRawData.isAbhaLinked).toLowerCase() === "true" ||
            (selectedRawData.maskedAbha && String(selectedRawData.maskedAbha).trim() && selectedRawData.maskedAbha !== "N/A") ||
            (selectedRawData.ABHANumber && String(selectedRawData.ABHANumber).trim() && selectedRawData.ABHANumber !== "N/A") ||
            (selectedRawData.abhaNumber && String(selectedRawData.abhaNumber).trim() && selectedRawData.abhaNumber !== "N/A")
        );

        const maskedAbha =
            selectedRawData.maskedAbha ||
            (selectedRawData.ABHANumber ? `**** ${String(selectedRawData.ABHANumber).slice(-4)}` : "") ||
            selectedRawData.formattedAbhaId ||
            "";

        const enrichedPatient = {
            ...selectedRawData,
            name: fullName,
            fullName: fullName,
            isAbhaLinked,
            isabhalinked: isAbhaLinked,
            maskedAbha,
            formattedAbhaId: maskedAbha || selectedRawData.ABHANumber || "",
            visitId:
                selectedRawData.visitId ||
                selectedRawData.visits?.[0]?.visitId ||
                selectedRawData.visits?.[0]?._id ||
                "",
        };

        setPatientData(enrichedPatient);
        navigate(`/billing?patientId=${encodeURIComponent(effectivePatientId)}`, {
            state: { patientData: enrichedPatient, patientId: effectivePatientId },
            replace: true,
        });
        fetchBillingForVisit(enrichedPatient);
    };

    const handleBackToPatientList = () => {
        setPatientData(null);
        setBillingId(null);
        setItems([createNewItem()]);
        navigate("/billing", { replace: true });
    };

    const handleAddItem = () => {
        setItems((prev) => [...prev, createNewItem()]);
    };

    const handleRemoveItem = (id) => {
        if (items.length > 1) {
            setItems((prev) => prev.filter((item) => item.id !== id));
        }
    };

    const handleInputChange = (id, field, value) => {
        setItems((prev) =>
            prev.map((item) => {
                if (item.id === id) {
                    const updatedItem = { ...item, [field]: value };

                    // Auto-calculate amount when rate or unit changes
                    if (field === 'rate' || field === 'unit') {
                        const rate = parseFloat(field === 'rate' ? value : item.rate) || 0;
                        const unit = parseFloat(field === 'unit' ? value : item.unit) || 0;
                        updatedItem.amount = rate * unit > 0 ? (rate * unit).toString() : '';
                    }

                    return updatedItem;
                }
                return item;
            })
        );
    };

    // Save billing via POST /api/billing (create) or PUT /api/billing/:id (update)
    const saveBilling = async () => {
        if (!canWriteBilling) {
            showError("You do not have permission to create or modify invoices.");
            return;
        }
        if (!patientData) {
            showWarning("No patient data available. Please select a patient first.");
            return;
        }

        const visitId = patientData?.visitId || patientData?.visits?.[0]?.visitId || "";
        if (!visitId) {
            showError("Visit ID is required to save billing.");
            return;
        }

        // Anti-tamper check: ensure line items do not contain executable markup
        const hasMarkup = items.some(
            (item) => containsExecutableMarkup(item.code) || containsExecutableMarkup(item.particulars)
        );
        if (hasMarkup) {
            showError("Line items contain invalid script or HTML markup. Please resolve errors before saving.");
            return;
        }

        const billingRows = items.filter(
            (item) => (item.particulars || "").trim() !== "" || (item.code || "").trim() !== ""
        );

        if (billingRows.length === 0) {
            showWarning("Please add at least one line item with code or particulars.");
            return;
        }

        const billings = billingRows.map((item) => {
            const rate = parseFloat(item.rate) || 0;
            const unit = parseFloat(item.unit) || 1;
            const amount = parseFloat(item.amount) || rate * unit;
            return {
                code: sanitizeText(item.code || "").trim() || "—",
                particulars: sanitizeText(item.particulars || "").trim() || "—",
                rate: rate,
                unit: unit,
                amount: amount,
            };
        });
        const totalAmount = billings.reduce((sum, b) => sum + (b.amount || 0), 0);

        setIsLoading(true);
        try {
            if (billingId) {
                const res = await updateBilling(billingId, deepSanitizePayload({
                    billings,
                    totalAmount,
                    status: "Draft",
                }));
                const data = res?.data || res;
                showSuccess(res?.message || "Billing updated successfully.");
                setBillingId(data?._id || billingId);
                const billingInvoiceData = {
                    patientData,
                    items: billingRows.map((r) => ({
                        slNo: r.slNo,
                        code: r.code,
                        particulars: r.particulars,
                        rate: r.rate,
                        unit: r.unit,
                        amount: r.amount,
                    })),
                    billNumber: data?._id ? `DCB-${String(data._id).slice(-8)}` : `DCB${Date.now()}`,
                    billingResponse: data,
                };
                sessionStorage.setItem("billingData", JSON.stringify(billingInvoiceData));
                setIsNavigatingAway(true);
                setTimeout(() => navigate("/billing-print-invoice", { state: { billingData: billingInvoiceData } }), 1500);
            } else {
                const res = await createBilling(deepSanitizePayload({
                    visitId,
                    billings,
                    totalAmount,
                    date: new Date().toISOString(),
                    status: "Draft",
                }));
                const data = res?.data || res;
                showSuccess(res?.message || "Billing created successfully.");
                if (data?._id) setBillingId(data._id);
                const billingInvoiceData = {
                    patientData,
                    items: billingRows.map((r) => ({
                        slNo: r.slNo,
                        code: r.code,
                        particulars: r.particulars,
                        rate: r.rate,
                        unit: r.unit,
                        amount: r.amount,
                    })),
                    billNumber: data?._id ? `DCB-${String(data._id).slice(-8)}` : `DCB${Date.now()}`,
                    billingResponse: data,
                };
                sessionStorage.setItem("billingData", JSON.stringify(billingInvoiceData));
                setIsNavigatingAway(true);
                setTimeout(() => navigate("/billing-print-invoice", { state: { billingData: billingInvoiceData } }), 1500);
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || "Failed to save billing.";
            showError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    // If no patient is selected, display the Billing Patient Selection List
    if (!patientData) {
        return <BillingPatientList onSelectPatient={handleSelectPatient} />;
    }

    return (
        <>
            <div className="container-fluid">
                <div className="billing-page">
                    <div className="allMainFeilds">
                        {/* Patient Selection Header Bar */}
                        <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                    onClick={handleBackToPatientList}
                                    title="Return to patient list"
                                >
                                    <i className="material-icons" style={{ fontSize: "18px" }}>arrow_back</i>
                                    <span>Back to Patient List</span>
                                </button>
                                <span className="text-muted d-none d-md-inline">|</span>
                                <span className="fw-bold text-primary" style={{ fontSize: "16px" }}>
                                    {patientData?.fullName || patientData?.name || "Patient"}
                                </span>
                                <span className="badge bg-light text-dark border">
                                    UHID: {patientData?.uhid || patientData?._id?.slice(-8) || "N/A"}
                                </span>
                                <span className="badge bg-light text-dark border">
                                    Visit ID: {patientData?.visitId || patientData?.visits?.[0]?.visitId || "N/A"}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                onClick={handleBackToPatientList}
                            >
                                <i className="material-icons" style={{ fontSize: "16px" }}>swap_horiz</i>
                                <span>Change Patient</span>
                            </button>
                        </div>

                        <ul className="nav nav-pills" role="tablist">
                            {/* <li className="nav-item">
                                <a className="nav-link " data-bs-toggle="pill" href="#home">OPD</a>
                            </li> */}
                            <li className="nav-item">
                                <a className="nav-link active" data-bs-toggle="pill" href="#menu1">Day Care</a>
                            </li>
                            {/* <li className="nav-item">
                                <a className="nav-link" data-bs-toggle="pill" href="#menu2">Other</a>
                            </li> */}
                        </ul>
                        {/* Tab panes */}
                        <div className="tab-content">
                            <div id="home" className="container tab-pane "><br />
                                OPD
                            </div>
                            <div id="menu1" className="container tab-pane active"><br />
                                <div className="basicDetails">
                                    <h4>Basic Details</h4>
                                    <div className="patientsFeildsRow">
                                        <div className="row">
                                            <div className="col-xs-12 col-md-3">
                                                <div className="feilds">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <label className="label mb-0">Patients</label>
                                                        <button
                                                            type="button"
                                                            onClick={handleBackToPatientList}
                                                            className="btn btn-link p-0 text-primary"
                                                            style={{ fontSize: "12px", textDecoration: "none" }}
                                                        >
                                                            Change
                                                        </button>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={patientData?.fullName || patientData?.name || ''}
                                                        readOnly
                                                        placeholder="Select a patient"
                                                        onClick={handleBackToPatientList}
                                                        style={{ cursor: "pointer", backgroundColor: "#fff" }}
                                                        title="Click to select another patient"
                                                        required />
                                                </div>
                                            </div>
                                            <div className="col-xs-12 col-md-3">
                                                <div className="feilds">
                                                    <label className="label">UHID</label>
                                                    <input
                                                        type="text"
                                                        value={patientData?.uhid || patientData?._id?.slice(-8) || ''}
                                                        readOnly
                                                        placeholder="UHID"
                                                        required />
                                                </div>
                                            </div>
                                            <div className="col-xs-12 col-md-3">
                                                <div className="feilds">
                                                    <label className="label">IPD ID *</label>
                                                    <input
                                                        type="text"
                                                        value={patientData?.visitId || patientData?._id?.slice(-8) || ''}
                                                        readOnly
                                                        placeholder="IPD ID"
                                                        required />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="basicDetails">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <h4 className="mb-0">Billings</h4>
                                        {!canWriteBilling && (
                                            <span className="badge bg-secondary px-2 py-1" style={{ fontSize: "12px" }}>
                                                Read-Only ({user?.role_name || "Viewing"})
                                            </span>
                                        )}
                                    </div>
                                    {!canWriteBilling && (
                                        <div
                                            className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2"
                                            role="alert"
                                            style={{ borderRadius: "8px", fontSize: "13px", backgroundColor: "#EBF5FF", color: "#1E429F", border: "1px solid #B4C6FC" }}
                                        >
                                            <i className="material-icons" style={{ fontSize: "18px" }}>visibility</i>
                                            <span><strong>Read-Only Mode:</strong> You do not have billing write permissions. Form inputs and invoice submission are locked.</span>
                                        </div>
                                    )}
                                    <div className="patientsFeildsRow">
                                        <div className="billingMain">
                                            {items.map((item) => (
                                                <div className="billinItemRow billinItemHeader" key={item.id}>
                                                    <div className="billingItem billingItemSLNo">
                                                        <div className="feilds">
                                                            <label className="label">SL No.</label>
                                                            <input
                                                                type="text"
                                                                defaultValue={item.slNo}
                                                                onChange={(e) => handleInputChange(item.id, "slNo", e.target.value)}
                                                                disabled={!canWriteBilling}
                                                                style={!canWriteBilling ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                                                required />
                                                        </div>
                                                    </div>
                                                    <div className="billingItem billingItemCode">
                                                        <div className="feilds">
                                                            <label className="label">Code</label>
                                                            <input
                                                                type="text"
                                                                value={item.code}
                                                                onChange={(e) => handleInputChange(item.id, "code", e.target.value)}
                                                                disabled={!canWriteBilling}
                                                                style={!canWriteBilling ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                                                required />
                                                        </div>
                                                    </div>
                                                    <div className="billingItem billingItemParticulars">
                                                        <div className="feilds">
                                                            <label className="label">Particulars</label>
                                                            <input
                                                                type="text"
                                                                value={item.particulars}
                                                                onChange={(e) =>
                                                                    handleInputChange(item.id, "particulars", e.target.value)
                                                                }
                                                                disabled={!canWriteBilling}
                                                                style={!canWriteBilling ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                                                required />
                                                        </div>
                                                    </div>
                                                    <div className="billingItem billingItemRate">
                                                        <div className="feilds">
                                                            <label className="label">Rate</label>
                                                            <input
                                                                type="text"
                                                                value={item.rate}
                                                                onChange={(e) => handleInputChange(item.id, "rate", e.target.value)}
                                                                disabled={!canWriteBilling}
                                                                style={!canWriteBilling ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                                                required />
                                                        </div>
                                                    </div>
                                                    <div className="billingItem billingItemUnit">
                                                        <div className="feilds">
                                                            <label className="label">Unit</label>
                                                            <input
                                                                type="text"
                                                                value={item.unit}
                                                                onChange={(e) => handleInputChange(item.id, "unit", e.target.value)}
                                                                disabled={!canWriteBilling}
                                                                style={!canWriteBilling ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                                                required />
                                                        </div>
                                                    </div>
                                                    <div className="billingItem billingItemAmt">
                                                        <div className="feilds">
                                                            <label className="label">Amount</label>
                                                            <input
                                                                type="text"
                                                                value={item.amount}
                                                                readOnly
                                                                placeholder="Amount"
                                                                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                                                required />
                                                        </div>
                                                    </div>
                                                    <div className="billingItem billingItemAction">
                                                        <div className="feilds">
                                                            <label className="label">&nbsp;</label>
                                                            {canWriteBilling && (
                                                                <div className="trashSection" onClick={() => handleRemoveItem(item.id)}>
                                                                    <img src={Trash} alt="Delete" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            <div className="add-items">
                                                {canWriteBilling ? (
                                                    <>
                                                        <button type="button" className="customeBtn btnLightBlueBorder me-2" onClick={handleAddItem}>+ Add Item</button>
                                                        <button
                                                            type="button"
                                                            className="customeBtn btnLightBlueBorder me-2"
                                                            onClick={saveBilling}
                                                            disabled={isLoading}
                                                            style={{
                                                                opacity: isLoading ? 0.6 : 1,
                                                                cursor: isLoading ? "not-allowed" : "pointer",
                                                            }}
                                                        >
                                                            {isLoading ? 'Sending...' : 'Send to ABHA'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="customeBtn btnEmpty"
                                                            onClick={handleBackToPatientList}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <div className="d-flex align-items-center justify-content-between w-100">
                                                        <div className="text-muted small py-2 d-flex align-items-center gap-1">
                                                            <i className="material-icons" style={{ fontSize: "16px" }}>lock</i>
                                                            <span>Invoice creation and edits are locked in read-only mode.</span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="customeBtn btnEmpty"
                                                            onClick={handleBackToPatientList}
                                                        >
                                                            Back to Patient List
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </>
    );
};

export default Billing;